import {
  Calendar,
  DateRangeType,
  DayOfWeek,
} from "@fluentui/react-calendar-compat";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { setHours, setMinutes } from "date-fns";
import React from "react";
import { getCyclicInputValue } from "../../../../utils/common/CommonHelperFunctions";
import { getCalendarDateRange } from "../../../../utils/common/DateHelperFunctions";
import ErrorMessage from "../../../common/error/ErrorMessage";
import AmdTimeInput from "../../../common/form-controls/uncontrolled/AmdTimeInput";

interface ICustomFilterProps {
  currentTime: Date;
  fromDate: Date;
  toDate: Date;
  onDateChange: (fromDate: Date, toDate: Date) => void;
  errorMsg: string;
}

const CustomFilter: React.FC<ICustomFilterProps> = ({
  currentTime,
  fromDate,
  toDate,
  onDateChange,
  errorMsg,
}) => {
  const { minDate, maxDate } = getCalendarDateRange(currentTime);
  const classes = useStyles();

  const fromHours = fromDate.getHours();
  const fromMinutes = fromDate.getMinutes();
  const toHours = toDate.getHours();
  const toMinutes = toDate.getMinutes();

  const handleFromDateChange = (date: Date) => {
    onDateChange(date, toDate);
  };

  const handleToDateChange = (date: Date) => {
    onDateChange(fromDate, date);
  };

  const setFromHoursHandler = (value: number) => {
    const fromHoursValue = getCyclicInputValue(0, 23, value);
    const newFromDate = setHours(fromDate, fromHoursValue);
    onDateChange(newFromDate, toDate);
  };

  const setFromMinuteHandler = (value: number) => {
    const fromMins = getCyclicInputValue(0, 59, value);
    const newFromDate = setMinutes(fromDate, fromMins);
    onDateChange(newFromDate, toDate);
  };

  const setToHoursHandler = (value: number) => {
    const toHoursValue = getCyclicInputValue(0, 23, value);
    const newToDate = setHours(toDate, toHoursValue);
    onDateChange(fromDate, newToDate);
  };

  const setToMinuteHandler = (value: number) => {
    const toMinutesValue = getCyclicInputValue(0, 59, value);
    const newToDate = setMinutes(toDate, toMinutesValue);
    onDateChange(fromDate, newToDate);
  };

  return (
    <section data-testid="custom-filter-option">
      <div className={classes.container}>
        <div className={classes.calendarContainer}>
          <h3 className={classes.label}>From Date</h3>
          <Calendar
            today={currentTime}
            dateRangeType={DateRangeType.Day}
            highlightCurrentMonth
            highlightSelectedMonth
            isDayPickerVisible
            showMonthPickerAsOverlay
            showGoToToday={false}
            firstDayOfWeek={DayOfWeek.Sunday}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDate={handleFromDateChange}
            value={fromDate}
            className={classes.calendar}
          />
          <AmdTimeInput
            className={classes.timeInput}
            hours={fromHours}
            setHours={setFromHoursHandler}
            mins={fromMinutes}
            setMins={setFromMinuteHandler}
            padHour
            padMinute
          />
        </div>
        <div className={classes.calendarContainer}>
          <h3 className={classes.label}>To Date</h3>
          <Calendar
            today={currentTime}
            dateRangeType={DateRangeType.Day}
            highlightCurrentMonth
            highlightSelectedMonth
            isDayPickerVisible
            showMonthPickerAsOverlay
            showGoToToday={false}
            firstDayOfWeek={DayOfWeek.Sunday}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDate={handleToDateChange}
            value={toDate}
            className={mergeClasses(classes.calendar, "pr-sm-2")}
          />
          <AmdTimeInput
            className={classes.timeInput}
            hours={toHours}
            setHours={setToHoursHandler}
            mins={toMinutes}
            setMins={setToMinuteHandler}
            padHour
            padMinute
          />
        </div>
      </div>
      {errorMsg.length > 0 && (
        <ErrorMessage errorMsg={errorMsg} className={classes.errorMsg} />
      )}
    </section>
  );
};

export default CustomFilter;

const useStyles = makeStyles({
  container: {
    display: "flex",
    gap: "20px",
  },
  label: {
    paddingLeft: "12px",
  },
  timeInput: {
    padding: "0px 20px",
  },
  calendarContainer: {
    flexGrow: 1,
  },
  errorMsg: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  calendar: {
    width: "100%",
    paddingRight: "10px",
    "& table": {
      width: "100%",
    },
    "& .fui-CalendarDay": {
      width: "100%",
    },
    "& .fui-CalendarDayGrid__dayIsToday": {
      margin: "auto",
    },
  },
});
